import {
  Box,
  Checkbox,
  FormControl,
  FormLabel,
  Input,
  Radio,
  RadioGroup,
  Tag,
  TagCloseButton,
  TagLabel,
  Text,
  VStack,
  Wrap,
  WrapItem,
} from '@chakra-ui/react';
import { FC, useCallback, useEffect, useState } from 'react';
import { CustomFieldValue } from '../../../types';
import PracticeSearchSelect from './PracticeSearchSelect';

interface ClosestUrgentCareSelectProps {
  field: CustomFieldValue | undefined;
  onChange: (field: CustomFieldValue) => void;
  fieldId?: string;
}

type LinkedPractice = {
  practiceId: string;
  practiceLabel: string;
};

type StoredPractice = {
  practiceId?: string;
  id?: string;
  practiceLabel?: string;
  label?: string;
};

const CLINIC_MODE = 'Clinic';
const FREE_TEXT_MODE = 'Free Text';
const NO_URGENT_CARE_MODE = 'No Urgent Care';

const readLinkedPractices = (
  field: CustomFieldValue | undefined
): LinkedPractice[] => {
  const practices = field?.metadata?.practices as
    | StoredPractice[]
    | undefined;
  if (!Array.isArray(practices)) return [];
  return practices
    .map((practice) => ({
      practiceId: practice.practiceId || practice.id || '',
      practiceLabel: practice.practiceLabel || practice.label || '',
    }))
    .filter((practice) => practice.practiceId);
};

const readFreeText = (field: CustomFieldValue | undefined): string => {
  const instructions = field?.metadata?.freeTextInstructions;
  return typeof instructions === 'string' ? instructions : '';
};

// selectionType is the source of truth for which mode the field is in
const readMode = (field: CustomFieldValue | undefined): string => {
  const selectionType = field?.metadata?.selectionType;
  if (selectionType === 'free_text') return FREE_TEXT_MODE;
  if (selectionType === 'none_in_market') return NO_URGENT_CARE_MODE;
  return CLINIC_MODE;
};

// Whether the "also link a clinic" option is on. Persisted so that a reload
// can be told apart from a local edit: the box can be checked before a clinic
// is picked, so the presence of a clinic alone cannot represent it.
const readClinicSearchEnabled = (
  field: CustomFieldValue | undefined
): boolean => {
  if (readMode(field) !== FREE_TEXT_MODE) return false;
  const enabled = field?.metadata?.clinicSearchEnabled;
  if (typeof enabled === 'boolean') return enabled;
  return readLinkedPractices(field).length > 0;
};

export const ClosestUrgentCareSelect: FC<
  ClosestUrgentCareSelectProps
> = ({ field, onChange, fieldId }) => {
  const [practices, setPractices] = useState<LinkedPractice[]>(() =>
    readLinkedPractices(field)
  );
  const [freeTextInstructions, setFreeTextInstructions] =
    useState<string>(() => readFreeText(field));
  const [mode, setMode] = useState<string>(() => readMode(field));
  const [freeTextWithClinicSearch, setFreeTextWithClinicSearch] =
    useState<boolean>(() => readClinicSearchEnabled(field));

  // Keep local state in sync when the field is (re)loaded from the API.
  // A cleared field (e.g. a form reset) resets this selector too, rather than
  // leaving the previous clinic and instructions on screen.
  useEffect(() => {
    const linkedPractices = readLinkedPractices(field);
    const instructions = readFreeText(field);
    setPractices(linkedPractices);
    setFreeTextInstructions(instructions);
    setMode(readMode(field));
    setFreeTextWithClinicSearch(readClinicSearchEnabled(field));
  }, [field]);

  const updateField = useCallback(
    (
      selectionMode: string,
      linkedPractices: LinkedPractice[],
      instructions: string,
      clinicSearchEnabled: boolean
    ) => {
      const updatedField: CustomFieldValue = {
        // Preserve existing IDs - these are critical for backend operations
        id: field?.id,
        fieldId: field?.fieldId || fieldId || '',
        rowId: field?.rowId || null,
        startDate: field?.startDate || null,
        endDate: field?.endDate || null,
        // The display value is the clinic name, falling back to the free text
        value:
          selectionMode === NO_URGENT_CARE_MODE
            ? 'No urgent cares in this market'
            : linkedPractices.map((p) => p.practiceLabel).join(', ') ||
              instructions ||
              '',
        metadata: {
          practices: linkedPractices,
          selectionType:
            selectionMode === FREE_TEXT_MODE
              ? 'free_text'
              : selectionMode === NO_URGENT_CARE_MODE
              ? 'none_in_market'
              : 'clinic',
          ...(selectionMode === FREE_TEXT_MODE && {
            freeTextInstructions: instructions,
            clinicSearchEnabled,
          }),
        },
      };
      onChange(updatedField);
    },
    [field, onChange, fieldId]
  );

  const handleModeChange = useCallback(
    (value: string) => {
      // Re-selecting the active mode must not reset the clinic or the free text
      if (value === mode) return;
      setMode(value);
      if (value === CLINIC_MODE) {
        setFreeTextInstructions('');
        setFreeTextWithClinicSearch(false);
        updateField(CLINIC_MODE, practices, '', false);
      } else if (value === NO_URGENT_CARE_MODE) {
        setFreeTextInstructions('');
        setFreeTextWithClinicSearch(false);
        updateField(NO_URGENT_CARE_MODE, [], '', false);
      } else {
        updateField(FREE_TEXT_MODE, [], freeTextInstructions, false);
      }
    },
    [updateField, mode, practices, freeTextInstructions]
  );

  const handleSelectPractice = useCallback(
    (practice: LinkedPractice) => {
      if (!practice.practiceId) return;
      if (practices.some((p) => p.practiceId === practice.practiceId)) return;
      const next = [...practices, practice];
      setPractices(next);
      updateField(mode, next, freeTextInstructions, freeTextWithClinicSearch);
    },
    [practices, updateField, mode, freeTextInstructions, freeTextWithClinicSearch]
  );

  const handleRemovePractice = useCallback(
    (practiceId: string) => {
      const next = practices.filter((p) => p.practiceId !== practiceId);
      setPractices(next);
      updateField(mode, next, freeTextInstructions, freeTextWithClinicSearch);
    },
    [practices, updateField, mode, freeTextInstructions, freeTextWithClinicSearch]
  );

  const handleFreeTextWithClinicSearchToggle = useCallback(
    (isChecked: boolean) => {
      setFreeTextWithClinicSearch(isChecked);
      if (isChecked) {
        // Keep the existing clinic if one is already linked
        updateField(
          FREE_TEXT_MODE,
          practices,
          freeTextInstructions,
          true
        );
      } else {
        setPractices([]);
        updateField(FREE_TEXT_MODE, [], freeTextInstructions, false);
      }
    },
    [updateField, practices, freeTextInstructions]
  );

  const handleFreeTextChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const instructions = e.target.value;
      setFreeTextInstructions(instructions);
      updateField(
        mode,
        practices,
        instructions,
        freeTextWithClinicSearch
      );
    },
    [updateField, mode, practices, freeTextWithClinicSearch]
  );

  const showClinicSearch =
    mode === CLINIC_MODE ||
    (mode === FREE_TEXT_MODE && freeTextWithClinicSearch);

  return (
    <Box>
      {/* Radio Selection */}
      <RadioGroup
        value={mode}
        onChange={handleModeChange}
        mb="1.5rem"
      >
        <VStack spacing={4} align="start" width="100%">
          <Box
            p="1rem 1.25rem"
            borderRadius="md"
            border="1px solid"
            borderColor="gray.200"
            bg="white"
            cursor="pointer"
            width="100%"
            _hover={{
              bg: 'gray.50',
              borderColor: 'gray.300',
              transform: 'translateY(-1px)',
              boxShadow: 'sm',
            }}
            transition="all 0.2s"
            onClick={() => handleModeChange(CLINIC_MODE)}
          >
            <Radio value={CLINIC_MODE} size="md" pointerEvents="none">
              <Box>
                <Text fontWeight="600" color="gray.700">
                  Clinic
                </Text>
                <Text fontSize="0.75rem" color="gray.500">
                  Link one or more urgent care clinics
                </Text>
              </Box>
            </Radio>
          </Box>

          <Box
            p="1rem 1.25rem"
            borderRadius="md"
            border="1px solid"
            borderColor="gray.200"
            bg="white"
            cursor="pointer"
            width="100%"
            _hover={{
              bg: 'gray.50',
              borderColor: 'gray.300',
              transform: 'translateY(-1px)',
              boxShadow: 'sm',
            }}
            transition="all 0.2s"
            onClick={() => handleModeChange(FREE_TEXT_MODE)}
          >
            <Radio
              value={FREE_TEXT_MODE}
              size="md"
              pointerEvents="none"
            >
              <Box>
                <Text fontWeight="600" color="gray.700">
                  Free Text
                </Text>
                <Text fontSize="0.75rem" color="gray.500">
                  Custom urgent care instructions
                </Text>
              </Box>
            </Radio>
          </Box>

          <Box
            p="1rem 1.25rem"
            borderRadius="md"
            border="1px solid"
            borderColor="gray.200"
            bg="white"
            cursor="pointer"
            width="100%"
            _hover={{
              bg: 'gray.50',
              borderColor: 'gray.300',
              transform: 'translateY(-1px)',
              boxShadow: 'sm',
            }}
            transition="all 0.2s"
            onClick={() => handleModeChange(NO_URGENT_CARE_MODE)}
          >
            <Radio
              value={NO_URGENT_CARE_MODE}
              size="md"
              pointerEvents="none"
            >
              <Box>
                <Text fontWeight="600" color="gray.700">
                  No Urgent Care
                </Text>
                <Text fontSize="0.75rem" color="gray.500">
                  No urgent cares in this market
                </Text>
              </Box>
            </Radio>
          </Box>
        </VStack>
      </RadioGroup>

      {/* Free Text with Clinic Search Option */}
      {mode === FREE_TEXT_MODE && (
        <Box
          mb="1rem"
          p="1rem"
          bg="blue.50"
          borderRadius="md"
          border="1px solid"
          borderColor="blue.200"
        >
          <Checkbox
            isChecked={freeTextWithClinicSearch}
            onChange={(e) =>
              handleFreeTextWithClinicSearchToggle(e.target.checked)
            }
            colorScheme="blue"
            size="md"
          >
            <Text fontSize="sm" fontWeight="500" color="blue.700">
              Also search and select a clinic?
            </Text>
          </Checkbox>
          <Text fontSize="0.75rem" color="blue.600" mt={1} ml={6}>
            This will allow you to specify both free text instructions
            and a linked clinic
          </Text>
        </Box>
      )}

      {/* Clinic Search Section */}
      {showClinicSearch && (
        <Box
          p="1.5rem"
          bg="gray.50"
          borderRadius="lg"
          border="1px solid"
          borderColor="gray.200"
          mb="1.5rem"
          _hover={{ borderColor: 'gray.300', boxShadow: 'sm' }}
          transition="all 0.2s"
        >
          <Text
            fontSize="sm"
            fontWeight="600"
            color="gray.700"
            mb="1rem"
          >
            Search and Select Clinic
          </Text>

          <PracticeSearchSelect
            field={{
              name: 'Closest-Urgent-Care',
              value: '',
              onChange: (value) => {
                if (Array.isArray(value)) return;
                handleSelectPractice({
                  practiceId: value.value,
                  practiceLabel: value.label,
                });
              },
            }}
            placeholder="Enter urgent care clinic name…"
            testId="closest-urgent-care-search"
          />

          {practices.length > 0 && (
            <Wrap mt={3} spacing={2}>
              {practices.map((practice) => (
                <WrapItem key={practice.practiceId}>
                  <Tag
                    size="md"
                    colorScheme="blue"
                    borderRadius="full"
                    variant="subtle"
                  >
                    <TagLabel>{practice.practiceLabel}</TagLabel>
                    <TagCloseButton
                      aria-label={`Remove ${practice.practiceLabel}`}
                      onClick={() =>
                        handleRemovePractice(practice.practiceId)
                      }
                    />
                  </Tag>
                </WrapItem>
              ))}
            </Wrap>
          )}
        </Box>
      )}

      {/* Free Text Instructions Input Field */}
      {mode === FREE_TEXT_MODE && (
        <Box
          p="1.5rem"
          bg="gray.50"
          borderRadius="lg"
          border="1px solid"
          borderColor="gray.200"
          _hover={{ borderColor: 'gray.300', boxShadow: 'sm' }}
          transition="all 0.2s"
        >
          <FormControl>
            <FormLabel
              fontSize="sm"
              fontWeight="600"
              color="gray.700"
              mb="0.75rem"
            >
              Free Text Instructions
            </FormLabel>
            <Input
              value={freeTextInstructions}
              onChange={handleFreeTextChange}
              placeholder="e.g., send to the nearest in-network urgent care, open until 8pm"
              size="md"
              _hover={{ borderColor: 'gray.400' }}
              _focus={{
                borderColor: 'blue.500',
                boxShadow: '0 0 0 1px var(--chakra-colors-blue-500)',
              }}
            />
            <Text fontSize="xs" color="gray.500" mt="0.5rem">
              Specify where to send patients when no single clinic
              applies.
            </Text>
          </FormControl>
        </Box>
      )}
    </Box>
  );
};
